import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./results.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { buildSearchEvent } from '~/modules/analytics/analyticsHelper';
import { buildNavigationUrl } from '~/modules/helpers/searchUrlHelper';
import { initialPageRenderedMetricsCheck } from '~/modules/services/metricsService';
import { i18nStore, selectSearchLabels, WireI18n } from '~/modules/stores/i18nStore';
import { getSearchKey, hasFilters, isKeywordLessWithNoFilters, searchStore, selectSearchContext, selectSearchParams, selectSearchResults, selectSearchTotals, shouldAttemptFetch } from '~/modules/stores/searchStore';
import { images } from '~/modules/utilities/assetUtilities';
import { SponsoredUrlIdentifier } from '~/modules/utilities/channelUtilities';
class SearchResults extends LightningElement {
  setSearchParams([searchParams, noKeywordNoFilters, isExploreAgentforce]) {
    if (searchParams) {
      const newKey = getSearchKey(searchParams);
      if (!noKeywordNoFilters && newKey !== this._searchKey || isExploreAgentforce) {
        this._searchKey = newKey;
        this.searchParams = searchParams;
        this.currentPage = 0;
        this.dismissedError = false;
        searchStore.setCurrentPage(0);
        this.setOrFetchSearchResults();
      }
    }
  }
  setOrFetchSearchResults() {
    const results = this.selectSearchResults();
    if (results != null) {
      this.setSearchResults(results);
    } else {
      this.fetchSearchResults();
    }
  }
  get keyword() {
    return this.searchParams?.keyword || '';
  }
  get maxDisplayableResults() {
    return this.currentPage === 0 ? this.pageSize : (this.currentPage + 1) * this.pageSize;
  }
  setSearchResultsFromStore(_) {
    const results = this.selectSearchResults();
    this.setSearchResults(results);
  }
  selectSearchResults() {
    return selectSearchResults(searchStore.getState(), this._searchKey);
  }
  setSearchResults(results) {
    const searchTotals = selectSearchTotals(searchStore.getState(), this.keyword);
    if (results) {
      if (results.listings) {
        this.searchResult = {
          ...results,
          listings: results.listings.slice(0, this.maxDisplayableResults)
        };
      } else {
        this.searchResult = results;
      }
      if (!this.isLoadingOrError) {
        this.dispatchEvent(buildSearchEvent(this.searchResult, this.getSearchType(), searchTotals));
      } else if (results.isError) {
        this.dismissedError = false;
        this.currentPage = Math.max(0, (results.listings?.length || 0) / this.pageSize - 1);
        searchStore.setCurrentPage(this.currentPage);
      }
    }
    if (searchTotals) {
      this.searchTotals = searchTotals;
    }
  }
  getSearchType() {
    return hasFilters(this.searchParams.filterParams) ? 'filter' : 'keyword';
  }
  get searchQueryUid() {
    return this.searchResult?.context?.searchQueryUid;
  }
  get showSponsoredTiles() {
    return this.sponsoredListings?.length > 0 && !this.isInitialError;
  }
  get showListingTiles() {
    return this.searchListings?.length > 0 && !this.isInitialError;
  }
  get searchListings() {
    return this.allSearchListings;
  }
  get sponsoredListings() {
    if (!this.searchResult?.featured) {
      return [];
    }
    return this.searchResult.featured.map(listing => ({
      ...listing,
      listingUrl: this.getListingUrl(listing)
    }));
  }
  get sponsoredTileGridProps() {
    return {
      format: 'listing',
      records: this.sponsoredListings,
      id: this.searchQueryUid,
      title: this.labels.listingTiles.sponsoredTitle
    };
  }
  get tileGridProps() {
    return {
      format: 'listing',
      records: this.searchListings,
      id: this.searchQueryUid,
      title: this.listingTilesTitle
    };
  }
  get listingTilesTitle() {
    return this.showSponsoredTiles ? this.labels.listingTiles.title : '';
  }
  handleSwitch() {
    const event = new CustomEvent('searchtypechange', {
      detail: {
        type: this.searchParams.type === 'consultants' ? undefined : 'consultants'
      }
    });
    this.dispatchEvent(event);
  }
  handleTilesClick(_) {
    // TODO Implement search experience
  }
  handleErrorClosed() {
    this.dismissedError = true;
  }
  async fetchSearchResults(searchQueryId) {
    await searchStore.fetchSearchResults(searchQueryId ? {
      ...this.searchParams,
      searchQueryId,
      prevSearchQueryId: undefined
    } : this.searchParams, this.currentPage, this.pageSize);
  }
  async handleLoadMore(e) {
    this.currentPage++;
    const searchResults = this.selectSearchResults();
    if (shouldAttemptFetch(searchResults, this.currentPage, this.pageSize)) {
      this.fetchSearchResults(searchResults?.context.searchQueryUid);
    } else {
      this.setSearchResults(searchResults);
    }
    this.navigatedWithKeyboard = e.pointerType === 'mouse';
    this.cardFocusIdx = this.pageSize * this.currentPage;
  }
  get noResults() {
    return this.searchTotals.apps === 0 && this.searchTotals.consultants === 0 || this.appsResultsEmpty && this.searchTotals.consultants === 0 || this.consultantsResultsEmpty && this.searchTotals.apps === 0;
  }
  get appsResultsEmpty() {
    return this.searchParams.type === 'apps' && (this.searchTotals.apps === 0 || this.searchResult.isEmpty);
  }
  get consultantsResultsEmpty() {
    return this.searchParams.type === 'consultants' && (this.searchTotals.consultants === 0 || this.searchResult.isEmpty);
  }
  get isSearchResultEmpty() {
    return this.searchResult && this.searchResult.isEmpty;
  }
  get amountOfResults() {
    return this.searchResult?.listings?.length || 0;
  }
  get totalSearchResults() {
    return this.searchResult?.totalSearchResults || 0;
  }
  get isLoading() {
    return !this.searchResult || this.searchResult.isLoading;
  }
  get isInitialLoading() {
    return !this.searchResult || this.searchResult.isLoading && this.amountOfResults === 0;
  }
  get isInitialError() {
    return this.searchResult?.isError && this.amountOfResults === 0;
  }
  get shouldShowErrorBanner() {
    return !this.isInitialError && this.searchResult?.isError && !this.dismissedError;
  }
  get isLoadingOrError() {
    return !this.searchResult || this.searchResult.isLoading || this.searchResult.isError;
  }
  get shouldShowLoadMore() {
    return this.amountOfResults > 0 && this.amountOfResults < this.totalSearchResults;
  }
  getChannelParam() {
    if (this.searchParams.keyword) {
      return SponsoredUrlIdentifier.SPONSORED_SEARCH;
    }
    return SponsoredUrlIdentifier.SPONSORED_EXPLORE;
  }
  getListingUrl(listing) {
    return listing.sponsored ? `${listing.listingUrl}&channel=${this.getChannelParam()}` : listing.listingUrl;
  }
  get allSearchListings() {
    if (!this.searchResult?.listings) {
      return [];
    }
    return this.searchResult.listings.map(listing => ({
      ...listing,
      listingUrl: this.getListingUrl(listing)
    }));
  }
  handleAnalytics(event) {
    // During loading or error state, clicks can't be on tiles
    if (this.isLoadingOrError) {
      event.stopPropagation();
      return;
    }
    event.detail.keyword = this.searchParams.keyword;
    event.detail.navigateTo = buildNavigationUrl(this.searchParams, {
      type: this.searchParams.type,
      resetState: true
    });
    if (event.detail.listing?.oafId) {
      const context = selectSearchContext(searchStore.getState(), this._searchKey, event.detail.listing.oafId);
      event.detail.searchContext = {
        ...context
      };
    }

    // Add for click events
    if (event.detail.eventType === 'click' && event.detail.searchContext) {
      event.detail.searchContext.eventAction = 'search_result_click';
    }
  }
  constructor() {
    super();
    this.currentPage = 0;
    this.pageSize = 12;
    this.searchParams = {
      keyword: ''
    };
    this.errorEmptyStateImageUrl = images.errorEmptyState;
    this.searchTotals = {
      apps: 0,
      consultants: 0
    };
    this.dismissedError = false;
    this.searchResult = void 0;
    this.isSearchPage = void 0;
    this.cardFocusIdx = undefined;
    this.navigatedWithKeyboard = false;
    this.labels = void 0;
    this.handleTilesRetry = () => {
      this.fetchSearchResults();
    };
    this.i18nState = i18nStore.getState().i18n;
  }
  get errorTitleLabel() {
    if (this.isSearchPage && this.searchParams.keyword) {
      return this.labels.error.title;
    }
    return undefined;
  }
  get errorDescriptionLabel() {
    if (this.isSearchPage && this.searchParams.keyword) {
      return this.labels.error.description;
    }
    return undefined;
  }
  renderedCallback() {
    initialPageRenderedMetricsCheck();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SearchResults, {
  publicProps: {
    isSearchPage: {
      config: 0
    }
  },
  track: {
    dismissedError: 1,
    searchResult: 1
  },
  wire: {
    setSearchParams: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: searchStore,
          selector: state => [selectSearchParams(state), isKeywordLessWithNoFilters(state), state.searchParams.isExploreAgentforce],
          equalityFn
        };
      }
    },
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectSearchLabels
        };
      }
    },
    setSearchResultsFromStore: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: searchStore,
          // @ts-expect-error * Automated Type Error Fix
          selector: ({
            searchResults,
            totals
          }) => ({
            searchResults,
            totals
          }),
          equalityFn
        };
      }
    }
  },
  fields: ["currentPage", "pageSize", "searchParams", "errorEmptyStateImageUrl", "searchTotals", "cardFocusIdx", "navigatedWithKeyboard", "handleTilesRetry"]
});
const __lwc_component_class_internal = _registerComponent(SearchResults, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;